import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { createApiUrl } from '../utils/apiConfig';
import GradeDisplay from './GradeDisplay';
import styles from './GradingModal.module.css';

const GradingModal = ({ 
  isOpen, 
  onClose, 
  activityType, 
  activityId, 
  activityName, 
  students = [], 
  onGradeSubmitted = null 
}) => {
  const [grades, setGrades] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showBulkGrading, setShowBulkGrading] = useState(false);
  const [bulkStars, setBulkStars] = useState(3);
  const [bulkFeedback, setBulkFeedback] = useState('');

  useEffect(() => {
    if (isOpen && activityId && activityType) {
      loadExistingGrades();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- loadExistingGrades is stable, run when isOpen/activityId/activityType change
  }, [isOpen, activityId, activityType]);

  const loadExistingGrades = async () => {
    try {
      setLoading(true);
      const endpoint = activityType === 'mission' 
        ? `/api/missions/${activityId}/grades`
        : `/api/lessons/${activityId}/grades`;
      
      const response = await axios.get(createApiUrl(endpoint));
      
      if (response.data.success) {
        const gradesMap = {};
        response.data.data.forEach(grade => {
          gradesMap[grade.studentId] = grade;
        });
        setGrades(gradesMap);
      }
    } catch (error) {
      console.error('Error loading grades:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGradeChange = (studentId, field, value) => {
    setGrades(prev => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        [field]: value
      }
    }));
  };

  const saveGrade = async (studentId) => {
    try {
      setSaving(true);
      const gradeData = grades[studentId];
      
      if (!gradeData) return;

      const payload = {
        studentId,
        stars: gradeData.stars || 0,
        emojiGrade: gradeData.emojiGrade || '👍',
        masteryLevel: gradeData.masteryLevel || 'Starting',
        feedback: gradeData.feedback || ''
      };

      let response;
      if (activityType === 'mission') {
        response = await axios.post(
          createApiUrl(`/api/missions/${activityId}/grade`),
          payload
        );
      } else {
        // For lessons, we need to find the assignment ID
        const assignment = students.find(s => s._id === studentId);
        if (assignment && assignment.assignmentId) {
          response = await axios.post(
            createApiUrl(`/api/lesson-assignments/${assignment.assignmentId}/grade`),
            payload
          );
        } else {
          throw new Error('Assignment ID not found');
        }
      }

      if (response.data.success) {
        setGrades(prev => ({
          ...prev,
          [studentId]: response.data.data
        }));
        
        if (onGradeSubmitted) {
          onGradeSubmitted(response.data.data);
        }
      }
    } catch (error) {
      console.error('Error saving grade:', error);
      alert('Error saving grade. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const saveBulkGrades = async () => {
    try {
      setSaving(true);
      const gradesToSave = students
        .filter(student => !grades[student._id] || !grades[student._id]._id)
        .map(student => ({
          studentId: student._id,
          stars: bulkStars,
          emojiGrade: getEmojiFromStars(bulkStars),
          masteryLevel: getMasteryFromStars(bulkStars),
          feedback: bulkFeedback
        }));

      if (gradesToSave.length === 0) {
        alert('No new grades to save.');
        return;
      }

      // Save each grade individually
      for (const gradeData of gradesToSave) {
        const payload = {
          studentId: gradeData.studentId,
          stars: gradeData.stars,
          emojiGrade: gradeData.emojiGrade,
          masteryLevel: gradeData.masteryLevel,
          feedback: gradeData.feedback
        };

        if (activityType === 'mission') {
          await axios.post(
            createApiUrl(`/api/missions/${activityId}/grade`),
            payload
          );
        } else {
          const assignment = students.find(s => s._id === gradeData.studentId);
          if (assignment && assignment.assignmentId) {
            await axios.post(
              createApiUrl(`/api/lesson-assignments/${assignment.assignmentId}/grade`),
              payload
            );
          }
        }
      }

      // Reload grades to update the UI
      await loadExistingGrades();
      
      setShowBulkGrading(false);
      setBulkFeedback('');
      
      if (onGradeSubmitted) {
        onGradeSubmitted(gradesToSave);
      }
    } catch (error) {
      console.error('Error saving bulk grades:', error);
      alert('Error saving bulk grades. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const getEmojiFromStars = (stars) => {
    const emojiMap = {
      0: '🔄', 1: '💪', 2: '👍', 3: '⭐', 4: '🌟', 5: '🏆'
    };
    return emojiMap[stars] || '👍';
  };

  const getMasteryFromStars = (stars) => {
    if (stars >= 5) return 'Master';
    if (stars >= 4) return 'Expert';
    if (stars >= 3) return 'Learning';
    if (stars >= 2) return 'Starting';
    return 'Not Started';
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2 className={styles.title}>
            <span className={styles.icon}>📝</span>
            Grade {activityType === 'mission' ? 'Mission' : activityType === 'quiz' ? 'Quiz' : 'Lesson'}: {activityName}
          </h2>
          <button className={styles.closeBtn} onClick={onClose}>×</button>
        </div>

        <div className={styles.content}>
          {loading ? (
            <div className={styles.loading}>
              <div className={styles.spinner}></div>
              <p>Loading grades...</p>
            </div>
          ) : (
            <>
              <div className={styles.actions}>
                <button
                  className={styles.bulkBtn}
                  onClick={() => setShowBulkGrading(!showBulkGrading)}
                >
                  <span className={styles.btnIcon}>⚡</span>
                  Bulk Grade All Students
                </button>
              </div>

              {showBulkGrading && (
                <div className={styles.bulkGrading}>
                  <h3>Bulk Grade All Students</h3>
                  <div className={styles.bulkControls}>
                    <div className={styles.starSelector}>
                      <label>Stars:</label>
                      <div className={styles.starsInput}>
                        {[0, 1, 2, 3, 4, 5].map(star => (
                          <button
                            key={star}
                            className={`${styles.starBtn} ${bulkStars === star ? styles.selected : ''}`}
                            onClick={() => setBulkStars(star)}
                          >
                            {star} ⭐
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    <div className={styles.feedbackInput}>
                      <label>Feedback (optional):</label>
                      <textarea
                        value={bulkFeedback}
                        onChange={(e) => setBulkFeedback(e.target.value)}
                        placeholder="Enter feedback for all students..."
                        rows="3"
                      />
                    </div>
                    
                    <div className={styles.bulkActions}>
                      <button
                        className={styles.saveBulkBtn}
                        onClick={saveBulkGrades}
                        disabled={saving}
                      >
                        {saving ? 'Saving...' : 'Save All Grades'}
                      </button>
                      <button
                        className={styles.cancelBtn}
                        onClick={() => setShowBulkGrading(false)}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className={styles.studentsList}>
                {students.map(student => {
                  const grade = grades[student._id] || {};
                  const hasGrade = grade._id;
                  
                  return (
                    <div key={student._id} className={styles.studentCard}>
                      <div className={styles.studentInfo}>
                        <h4 className={styles.studentName}>
                          {student.contactName || student.firstName + ' ' + student.lastName}
                        </h4>
                        {(student.yearGrade || student.grade) && (
                          <span className={styles.yearGrade}>
                            Year {student.yearGrade || student.grade}
                          </span>
                        )}
                      </div>

                      <div className={styles.gradeSection}>
                        <div className={styles.starSelector}>
                          <label>Stars:</label>
                          <div className={styles.starsInput}>
                            {[0, 1, 2, 3, 4, 5].map(star => (
                              <button
                                key={star}
                                className={`${styles.starBtn} ${
                                  (grade.stars || 0) === star ? styles.selected : ''
                                }`}
                                onClick={() => handleGradeChange(student._id, 'stars', star)}
                              >
                                {star} ⭐
                              </button>
                            ))}
                          </div>
                        </div>

                        <div className={styles.feedbackInput}>
                          <label>Feedback:</label>
                          <textarea
                            value={grade.feedback || ''}
                            onChange={(e) => handleGradeChange(student._id, 'feedback', e.target.value)}
                            placeholder="Enter encouraging feedback..."
                            rows="2"
                          />
                        </div>

                        <div className={styles.gradePreview}>
                          <GradeDisplay
                            grade={{
                              stars: grade.stars || 0,
                              emojiGrade: grade.emojiGrade || getEmojiFromStars(grade.stars || 0),
                              masteryLevel: grade.masteryLevel || getMasteryFromStars(grade.stars || 0),
                              feedback: grade.feedback
                            }}
                            gradeLevel="primary"
                            size="small"
                            showDetails={true}
                          />
                        </div>

                        <button
                          className={styles.saveBtn}
                          onClick={() => saveGrade(student._id)}
                          disabled={saving}
                        >
                          {saving ? 'Saving...' : hasGrade ? 'Update Grade' : 'Save Grade'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GradingModal;
